import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Lock, ArrowRight, Eye, EyeOff, Building2, Users, ChevronDown, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = [
  { value: 'dm_channel', label: 'DM Channel', roles: ['dm', 'adm', 'cp'], hasHierarchy: true },
  { value: 'marketing', label: 'Marketing', roles: ['employee'], sections: ['barter', 'tele_calling', 'digital_marketing', 'marketing_sub_dept', 'reception'] },
  { value: 'admin', label: 'Admin Department', roles: ['employee', 'department_head'] },
  { value: 'hr', label: 'Human Resources', roles: ['employee'] },
  { value: 'legal', label: 'Legal Department', roles: ['employee'] },
  { value: 'finance', label: 'Finance Department', roles: ['employee'] },
  { value: 'operations', label: 'Operations & Development', roles: ['employee'] },
  { value: 'architect', label: 'Architect Department', roles: ['employee'] },
  { value: 'it', label: 'IT Department', roles: ['employee'] },
  { value: 'customer', label: 'Customer', roles: ['customer'] }
];

const ROLE_LABELS = {
  'dm': 'Development Manager',
  'adm': 'Assistant Development Manager',
  'cp': 'Channel Partner',
  'employee': 'Employee',
  'department_head': 'Department Head',
  'customer': 'Customer'
};

const SECTION_LABELS = {
  'barter': 'Barter',
  'tele_calling': 'Tele Calling',
  'digital_marketing': 'Digital Marketing',
  'marketing_sub_dept': 'Marketing Sub Department',
  'reception': 'Reception'
};

const SignUp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    department: '',
    role: '',
    section: '',
    supervisorId: '',
    otp: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleProcessed = useRef(false);

  // ── EMERGENT GOOGLE AUTH CALLBACK HANDLER ──
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH. The redirect uses window.location.origin only.
  useEffect(() => {
    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    if (googleProcessed.current) return;
    googleProcessed.current = true;

    const sessionId = decodeURIComponent(match[1]);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    setGoogleLoading(true);
    (async () => {
      try {
        const resp = await axios.post(`${API}/crm/auth/google/exchange`, { session_id: sessionId });
        const { token, user } = resp.data;
        localStorage.setItem('crm_token', token);
        localStorage.setItem('crm_user', JSON.stringify(user));
        toast.success(`Welcome, ${user.firstName}!`);
        navigate('/dashboard/main');
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        setError(detail || 'Google sign-in failed. Please complete sign-up using email + password above.');
        toast.error('Google sign-in failed. Use email + password sign-up instead.');
      } finally {
        setGoogleLoading(false);
      }
    })();
  }, [navigate]);

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS.
    const redirectUrl = window.location.origin + '/signup';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSelectChange = (name, value) => {
    if (name === 'department') {
      setFormData({ ...formData, department: value, role: '', section: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const selectedDept = DEPARTMENTS.find(d => d.value === formData.department);
  const availableRoles = selectedDept?.roles || [];
  const availableSections = selectedDept?.sections || [];
  const needsSupervisor = ['adm', 'cp'].includes(formData.role) && formData.department === 'dm_channel';
  const needsSection = formData.department === 'marketing';

  const sendOTP = async () => {
    if (!formData.phone || formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setLoading(true);
    try {
      const phone = formData.phone.startsWith('+') ? formData.phone : `+91${formData.phone}`;
      const response = await axios.post(`${API}/crm/send-otp`, { phone });
      setOtpSent(true);
      toast.success('OTP sent to your phone!');
      
      // Extract OTP from dev mode message for testing
      const match = response.data.message.match(/Dev mode: (\d+)/);
      if (match) {
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const phone = formData.phone.startsWith('+') ? formData.phone : `+91${formData.phone}`;
      await axios.post(`${API}/crm/verify-otp`, { phone, code: formData.otp });
      setOtpVerified(true);
      toast.success('Phone verified successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const phone = formData.phone.startsWith('+') ? formData.phone : `+91${formData.phone}`;
      const payload = {
        firstName: formData.firstName,
        middleName: formData.middleName || null,
        lastName: formData.lastName,
        email: formData.email,
        phone: phone,
        password: formData.password,
        department: formData.department,
        role: formData.role || (formData.department === 'customer' ? 'customer' : 'employee'),
        section: needsSection ? formData.section : null,
        supervisorId: needsSupervisor ? formData.supervisorId : null
      };
      
      const response = await axios.post(`${API}/crm/signup`, payload);
      
      toast.success(`Account created! Your Employee ID: ${response.data.employeeId}`);
      
      if (response.data.status === 'active') {
        navigate('/signin');
      } else {
        // Show pending approval message
        navigate('/signin', { state: { message: 'Your account is pending approval. You will be notified once approved.' } });
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  return (
    <div data-testid="signup-page" className="min-h-screen pt-20">
      <section
        className="relative min-h-[calc(100vh-80px)] flex items-center justify-center py-20 px-6"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1710746055183-ba55e616bfe8?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-emerald-900/80" />
        
        <div className="relative z-10 w-full max-w-lg">
          <div className="bg-white p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-playfair font-bold text-stone-900 mb-2">Create Account</h1>
              <p className="text-stone-600">Join DBK Group Of Industries</p>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    step >= s ? 'bg-emerald-900 text-white' : 'bg-stone-200 text-stone-500'
                  }`}>
                    {s}
                  </div>
                  {s < 3 && (
                    <div className={`w-12 h-1 mx-1 transition-colors ${
                      step > s ? 'bg-emerald-900' : 'bg-stone-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Personal Information */}
              {step === 1 && (
                <div className="space-y-4" data-testid="signup-step-1">
                  <h2 className="text-lg font-bold text-stone-900 mb-4">Personal Information</h2>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">First Name *</label>
                      <Input
                        name="firstName"
                        placeholder="First"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        data-testid="signup-firstname"
                        className="h-11 border-stone-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Middle Name</label>
                      <Input
                        name="middleName"
                        placeholder="Middle"
                        value={formData.middleName}
                        onChange={handleChange}
                        data-testid="signup-middlename"
                        className="h-11 border-stone-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Last Name *</label>
                      <Input
                        name="lastName"
                        placeholder="Last"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        data-testid="signup-lastname"
                        className="h-11 border-stone-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        type="email"
                        name="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="signup-email"
                        className="pl-10 h-11 border-stone-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Phone Number *</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <Input
                          type="tel"
                          name="phone"
                          placeholder="9876543210"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          disabled={otpVerified}
                          data-testid="signup-phone"
                          className="pl-10 h-11 border-stone-300"
                        />
                      </div>
                      {!otpVerified && (
                        <Button
                          type="button"
                          onClick={sendOTP}
                          disabled={loading || !formData.phone}
                          className="h-11 bg-amber-600 hover:bg-amber-700 text-white text-xs px-4"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : otpSent ? 'Resend' : 'Send OTP'}
                        </Button>
                      )}
                      {otpVerified && (
                        <div className="h-11 px-3 flex items-center bg-green-100 text-green-700 rounded text-xs font-medium">
                          <CheckCircle className="w-4 h-4 mr-1" /> Verified
                        </div>
                      )}
                    </div>
                  </div>

                  {otpSent && !otpVerified && (
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Enter OTP *</label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          name="otp"
                          placeholder="6-digit OTP"
                          value={formData.otp}
                          onChange={handleChange}
                          maxLength={6}
                          data-testid="signup-otp"
                          className="h-11 border-stone-300 flex-1"
                        />
                        <Button
                          type="button"
                          onClick={verifyOTP}
                          disabled={loading || formData.otp.length !== 6}
                          className="h-11 bg-emerald-900 hover:bg-emerald-800 text-white text-xs px-4"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                        </Button>
                      </div>
                      <p className="text-xs text-stone-500 mt-1">OTP valid for 5 minutes</p>
                    </div>
                  )}

                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={!otpVerified}
                    data-testid="signup-next-1"
                    className="w-full bg-emerald-900 hover:bg-emerald-800 text-white h-11 uppercase tracking-widest text-xs font-bold mt-6 disabled:opacity-50"
                  >
                    Continue <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Step 2: Department & Role */}
              {step === 2 && (
                <div className="space-y-4" data-testid="signup-step-2">
                  <h2 className="text-lg font-bold text-stone-900 mb-4">Department & Role</h2>
                  
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Department *</label>
                    <Select value={formData.department} onValueChange={(v) => handleSelectChange('department', v)}>
                      <SelectTrigger className="h-11 border-stone-300" data-testid="signup-department">
                        <SelectValue placeholder="Select your department" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map((dept) => (
                          <SelectItem key={dept.value} value={dept.value}>
                            {dept.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.department === 'dm_channel' && (
                      <p className="text-xs text-emerald-700 mt-1 bg-emerald-50 p-2 rounded">
                        DM Channel: Development Manager → Assistant Development Manager → Channel Partner → Customer hierarchy
                      </p>
                    )}
                  </div>

                  {needsSection && (
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Section *</label>
                      <Select value={formData.section} onValueChange={(v) => handleSelectChange('section', v)}>
                        <SelectTrigger className="h-11 border-stone-300" data-testid="signup-section">
                          <SelectValue placeholder="Select your section" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableSections.map((section) => (
                            <SelectItem key={section} value={section}>
                              {SECTION_LABELS[section] || section}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {formData.department && !needsSection && (
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Role *</label>
                      <Select value={formData.role} onValueChange={(v) => handleSelectChange('role', v)}>
                        <SelectTrigger className="h-11 border-stone-300" data-testid="signup-role">
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRoles.map((role) => (
                            <SelectItem key={role} value={role}>
                              {ROLE_LABELS[role] || role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {needsSection && formData.section && (
                    <div className="p-3 bg-stone-50 border border-stone-200 rounded">
                      <p className="text-xs text-stone-600">
                        <span className="font-medium">Role:</span> Marketing Staff - {SECTION_LABELS[formData.section] || formData.section}
                      </p>
                    </div>
                  )}

                  {needsSupervisor && (
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">
                        Supervisor's Employee ID *
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <Input
                          name="supervisorId"
                          placeholder="e.g., DBK-DMC-2026-0001"
                          value={formData.supervisorId}
                          onChange={handleChange}
                          required={needsSupervisor}
                          data-testid="signup-supervisor"
                          className="pl-10 h-11 border-stone-300"
                        />
                      </div>
                      <p className="text-xs text-stone-500 mt-1">
                        {formData.role === 'ADM' && 'Enter your Development Manager\'s Employee ID'}
                        {formData.role === 'CP' && 'Enter your Assistant Development Manager\'s Employee ID'}
                        {formData.role === 'Customer' && 'Enter your Channel Partner\'s Employee ID'}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 mt-6">
                    <Button
                      type="button"
                      onClick={prevStep}
                      variant="outline"
                      className="flex-1 h-11 border-stone-300 text-stone-700 uppercase tracking-widest text-xs font-bold"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={nextStep}
                      data-testid="signup-next-2"
                      className="flex-1 bg-emerald-900 hover:bg-emerald-800 text-white h-11 uppercase tracking-widest text-xs font-bold"
                    >
                      Continue <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Password */}
              {step === 3 && (
                <div className="space-y-4" data-testid="signup-step-3">
                  <h2 className="text-lg font-bold text-stone-900 mb-4">Set Password</h2>
                  
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        data-testid="signup-password"
                        className="pl-10 pr-10 h-11 border-stone-300"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">
                      Min 8 characters with uppercase, lowercase, number, and special character
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Confirm Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        data-testid="signup-confirm-password"
                        className="pl-10 h-11 border-stone-300"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-2 mt-4">
                    <input type="checkbox" required className="mt-1 rounded border-stone-300 text-emerald-900" />
                    <p className="text-xs text-stone-600">
                      I agree to the <Link to="/terms" className="text-emerald-900 underline">Terms of Service</Link> and{' '}
                      <Link to="/privacy" className="text-emerald-900 underline">Privacy Policy</Link>
                    </p>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      type="button"
                      onClick={prevStep}
                      variant="outline"
                      className="flex-1 h-11 border-stone-300 text-stone-700 uppercase tracking-widest text-xs font-bold"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="signup-submit"
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-11 uppercase tracking-widest text-xs font-bold"
                    >
                      {loading ? 'Creating...' : 'Create Account'}
                    </Button>
                  </div>
                </div>
              )}
            </form>

            {/* ── ERROR DISPLAY ─────────────────────────────── */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-xs" data-testid="signup-error">
                {error}
              </div>
            )}

            {/* ── OR DIVIDER + GOOGLE WORKSPACE SIGN-IN ─────── */}
            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1 border-t border-stone-200" />
              <span className="text-xs text-stone-400 uppercase tracking-widest">OR</span>
              <div className="flex-1 border-t border-stone-200" />
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              data-testid="signup-google-btn"
              className="mt-4 w-full h-11 border border-stone-300 rounded flex items-center justify-center gap-3 hover:bg-stone-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {googleLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-stone-600" />
                  <span className="text-sm font-medium text-stone-700">Signing in with Google…</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                  </svg>
                  <span className="text-sm font-medium text-stone-700">Sign up with Google Workspace</span>
                </>
              )}
            </button>

            <p className="mt-2 text-center text-xs text-stone-500">
              Only works with your <strong>@siddhivinayakinfra.estate</strong> Google Workspace account.<br />
              <span className="text-amber-700">New users:</span> use Email + Password sign-up above. Existing Workspace users can use this button to sign in.
            </p>

            <div className="mt-8 pt-6 border-t border-stone-200 text-center">
              <p className="text-stone-600">
                Already have an account?{' '}
                <Link to="/signin" className="text-emerald-900 hover:text-emerald-800 font-bold">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignUp;
